/**
 * The Brain — /brain
 *
 * A living neural map of the entire Defera organism, rendered as a brain in
 * side profile. Every node is a real system component:
 *
 *   - The 21 AI staff are neurons, clustered into anatomical lobes by what
 *     their department actually does (concierge = temporal/language, risk =
 *     frontal/judgment, insights = parietal/integration, search = occipital/
 *     vision, booking ops = cerebellum/motor, payment ops = brainstem/
 *     autonomic, loyalty = limbic/reward, security = the deep amygdala).
 *   - Supabase is the memory core every mind converges on.
 *   - The 22 Vercel cron schedules are pacemaker ganglia along the brainstem,
 *     firing on compressed rhythms.
 *   - External services (Stripe, NMI, Resend, Search Engines 1 + 2, OpenAI,
 *     Claude, Cartesia, Vercel) are peripheral organs on long axons.
 *   - Customer-facing surfaces are the senses; Customers and Founders anchor
 *     the outside world.
 *
 * Edges are real flows (reporting lines, webhook ingestion, cron triggers,
 * model substrates), and the traveling pulses + synapse log dramatize them.
 *
 * Standalone route — deliberately rendered OUTSIDE Layout so there is no nav
 * and no tabs anywhere. Admin-only: the same JWT app_metadata.roles gate as
 * Staff/Activity (users cannot self-edit app_metadata), and the only data
 * fetch (/api/admin/staff, used to overlay live titles/summaries on
 * tooltips) is requireAdmin-gated server-side.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Brain as BrainIcon, ShieldAlert, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { User as UserEntity } from '@/api/entities';
import { Button } from '@/components/ui/button';

// ---------------------------------------------------------------------------
// Access — mirrors Staff.jsx / Layout.jsx / Activity.jsx.
// ---------------------------------------------------------------------------

function isCurrentUserAdmin(u) {
  if (!u) return false;
  const roles = u?.app_metadata?.roles || u?.appMetadata?.roles || [];
  return (Array.isArray(roles) && roles.includes('admin')) || u?.role === 'admin';
}

// ---------------------------------------------------------------------------
// The map. All coordinates live on a 1000×800 stage that gets fitted to the
// viewport. Hand-placed so the silhouette and clusters stay art-directed.
// ---------------------------------------------------------------------------

const STAGE_W = 1000;
const STAGE_H = 800;

// Side-profile silhouette, facing right (toward the customers it serves).
// Smoothed with Catmull-Rom at draw time; also used for dust containment.
const BRAIN_OUTLINE = [
  [795, 330], [802, 288], [786, 238], [742, 188], [676, 148], [596, 120],
  [506, 106], [420, 116], [338, 136], [272, 176], [224, 228], [196, 288],
  [188, 348], [196, 408], [218, 452], [244, 484], [232, 508], [248, 546],
  [292, 576], [352, 576], [392, 552], [398, 598], [404, 642], [446, 648],
  [452, 602], [458, 556], [504, 560], [548, 556], [606, 540], [656, 514],
  [704, 478], [748, 430], [778, 382],
];

// Faint interior folds that make the silhouette read as cortex, not a blob.
const SULCI = [
  [[545, 128], [552, 200], [560, 268], [552, 330]],           // central sulcus
  [[648, 448], [560, 462], [488, 478]],                        // lateral sulcus
  [[308, 186], [294, 252], [306, 312]],                        // parieto-occipital
  [[390, 548], [322, 524], [252, 490]],                        // cerebellum crease
  [[700, 200], [672, 258], [676, 316]],                        // frontal gyrus
  [[512, 522], [566, 532], [618, 516]],                        // temporal gyrus
  [[418, 152], [450, 198], [438, 248]],                        // parietal gyrus
  [[350, 170], [368, 232], [352, 296]],                        // occipital gyrus
  [[606, 168], [590, 224], [602, 282]],                        // prefrontal gyrus
];

// Department → lobe tint. Muted hues on the royal-blue field so the map
// stays cohesive; each department is still tellable apart.
const DEPT_TINTS = {
  concierge: [143, 182, 255],
  'risk-intelligence': [180, 160, 245],
  'finance-accounting': [229, 192, 123],
  'customer-insights': [125, 214, 201],
  'lifecycle-marketing': [240, 165, 190],
  'loyalty-rewards': [239, 201, 127],
  'payment-operations': [134, 216, 168],
  'booking-operations': [133, 200, 232],
  'security-reliability': [242, 144, 124],
};

const DEPT_LOBES = {
  concierge: 'Temporal lobe — language',
  'risk-intelligence': 'Frontal lobe — judgment',
  'finance-accounting': 'Frontal pole — planning',
  'customer-insights': 'Parietal lobe — integration',
  'lifecycle-marketing': 'Frontotemporal junction — speech production',
  'loyalty-rewards': 'Limbic system — reward',
  'payment-operations': 'Brainstem — autonomic function',
  'booking-operations': 'Cerebellum — motor coordination',
  'security-reliability': 'Amygdala — threat detection',
};

// Static roster snapshot (kept in sync with server/ai-staff.js). When the
// admin API is reachable, live titles/summaries overlay these in tooltips —
// positions and wiring stay art-directed either way.
const STAFF = [
  { id: 'stephanie', name: 'Stephanie', title: 'AI Concierge Quality Manager', dept: 'concierge', manager: true, x: 556, y: 388, detail: "Tommy's manager. Audits every concierge reply for factual accuracy and tone drift, files corrections, and voices support@ for founder-facing flows." },
  { id: 'tommy', name: 'Tommy', title: 'AI Concierge Specialist', dept: 'concierge', x: 492, y: 442, detail: 'Front-line concierge on /support — answers everything about products, payments, trips, seats, and cancellations, grounded in the knowledge base with live account context.' },
  { id: 'camille', name: 'Camille', title: 'AI Concierge Specialist', dept: 'concierge', x: 546, y: 420, detail: 'Concierge specialist on the same desk as Tommy — identical training and knowledge base. The desk rotates between visits.' },
  { id: 'andre', name: 'Andre', title: 'AI Concierge Specialist', dept: 'concierge', x: 592, y: 436, detail: 'Concierge specialist on the rotating desk — any specialist may pick up any customer thread.' },
  { id: 'isabella', name: 'Isabella', title: 'AI Concierge Specialist', dept: 'concierge', x: 622, y: 470, detail: 'Concierge specialist on the rotating desk — any specialist may pick up any customer thread.' },
  { id: 'kai', name: 'Kai', title: 'AI Concierge Specialist', dept: 'concierge', x: 560, y: 506, detail: 'Concierge specialist on the rotating desk — any specialist may pick up any customer thread.' },
  { id: 'elliott', name: 'Elliott', title: 'AI Risk Intelligence Director', dept: 'risk-intelligence', manager: true, x: 668, y: 262, detail: "Vera's, Marcus's and Roman's manager. Daily 11:00 UTC risk report — portfolio posture, the default ladder in aggregate, and whether the intelligence pipelines themselves are alive." },
  { id: 'vera', name: 'Vera', title: 'AI Risk Analyst', dept: 'risk-intelligence', x: 612, y: 300, detail: 'Scores every active installment booking for default and fare-spike risk, runs Risk Review scans, and reads Stripe Radar signals. Model Performance is her scorecard.' },
  { id: 'marcus', name: 'Marcus', title: 'AI Portfolio Analyst', dept: 'risk-intelligence', x: 700, y: 342, detail: 'Builds the 07:30 UTC Portfolio Intelligence snapshots — health score, liquidity coverage, receivables vs. liabilities, stress tests.' },
  { id: 'roman', name: 'Roman', title: 'AI Collections & Recovery Specialist', dept: 'risk-intelligence', x: 630, y: 330, detail: 'Collections cases from open to recovered or written off, the passenger blacklist, and the road back — reinstatements and credit resolutions.' },
  { id: 'bernard', name: 'Bernard', title: 'AI Corporate Controller', dept: 'finance-accounting', manager: true, x: 740, y: 292, detail: "The company's books, daily at 12:30 UTC — own revenue vs. pass-through airfare, cash actually landed vs. billed, and what Defera owes customers against cash on hand." },
  { id: 'iris', name: 'Iris', title: 'AI Conversion Strategist', dept: 'customer-insights', manager: true, x: 468, y: 184, detail: "Ezra's and Priya's manager. Knows where every surface lives in the app; connects behavior patterns to the exact UI involved and files refinement recommendations, daily at 10:00 UTC." },
  { id: 'ezra', name: 'Ezra', title: 'AI Behavioral Insights Lead', dept: 'customer-insights', x: 408, y: 224, detail: 'Studies how customers actually move — stalled checkouts, abandoned searches, questions the product forced them to ask. Daily 09:00 UTC digest feeds Iris.' },
  { id: 'priya', name: 'Priya', title: 'AI Search Quality Analyst', dept: 'customer-insights', x: 526, y: 222, detail: 'Watches every search: volumes, routes, which engine answered, and zero-result searches — a customer who wanted to pay and got a shrug. Benchmarks every 2 hours.' },
  { id: 'margot', name: 'Margot', title: 'AI Lifecycle Marketing Manager', dept: 'lifecycle-marketing', manager: true, x: 658, y: 422, detail: 'Owns every automated customer email — welcome, day-7 nurture, receipts, notices. Daily 12:00 UTC report tracks sends and, with open tracking live, whether anyone reads them.' },
  { id: 'miles', name: 'Miles', title: 'AI Loyalty Program Manager', dept: 'loyalty-rewards', manager: true, x: 508, y: 320, detail: 'Keeper of Defera Miles and status perks — every earn and redemption in the ledger, and the daily birthday-credit run. Generous in feel, exactly balanced in fact.' },
  { id: 'audrey', name: 'Audrey', title: 'AI Operations Auditor', dept: 'payment-operations', manager: true, x: 438, y: 556, detail: "Penny's manager. Every hour at :45 she verifies the work actually happened — cron heartbeats, webhook processing, email sends on record, and no default without an independent re-check." },
  { id: 'penny', name: 'Penny', title: 'AI Payments Operations Specialist', dept: 'payment-operations', x: 424, y: 592, detail: 'Keeps installments flowing: reminders 13:00 UTC, the NMI charge run 14:00, default warnings 15:00, plus 15-minute reconcilers all day.' },
  { id: 'owen', name: 'Owen', title: 'AI Booking Operations Manager', dept: 'booking-operations', manager: true, x: 310, y: 544, detail: 'Owns what happens after the money — every confirmed trip still waiting on an airline confirmation sits in his queue; seats, changes, departures, and the Faye travel insurance queue are his to keep clean.' },
  { id: 'silas', name: 'Silas', title: 'AI Application Security Auditor', dept: 'security-reliability', manager: true, x: 388, y: 298, detail: 'Sweeps the whole app from the outside 4× daily — availability, security headers, admin/cron auth gates, anonymous-RLS exposure, TLS. Finds and reports; never changes anything.' },
  { id: 'wren', name: 'Wren', title: 'AI Reliability Engineer', dept: 'security-reliability', x: 362, y: 396, detail: 'Daily sweep for the bugs customers would find first — grounding parity, uncollected balances, stuck queues, thrice-asked questions. Traces each to the code that caused it. (Wrens eat bugs.)' },
];

const CORE = {
  id: 'supabase', name: 'Supabase', title: 'Memory core — Postgres + RLS', x: 452, y: 352,
  detail: 'Every mind converges here. Bookings, ledgers, transcripts, heartbeats, findings — 130+ migrations of long-term memory, guarded row-by-row by RLS. Also runs the 72 edge-function reflex arcs.',
};

const SURFACES = [
  { id: 'chat-desk', name: 'Concierge desk', title: 'Customer chat — /support', x: 556, y: 470, detail: 'The rotating specialist desk. Grounds on the customer’s own trips, introduces itself by name, and hands unsolvable threads to the human queue.' },
  { id: 'voice-desk', name: 'Voice desk', title: 'Phone concierge — Cartesia line', x: 612, y: 524, detail: 'The concierge desk by phone — chat-desk parity by construction, fetching the same live prompt per call.' },
  { id: 'search', name: 'Search', title: 'Flight discovery — /search', x: 282, y: 356, detail: 'The eyes. Search Engine 1 primary with hidden inventory, Search Engine 2 deep-search fallback, zero-result telemetry to Priya.' },
  { id: 'checkout', name: 'Checkout rails', title: 'Capital · Zero · Reserve', x: 600, y: 360, detail: 'The motor strip. Three installment rails across Stripe and NMI — 57 edge functions of scheduling, previews, charges, and recovery.' },
  { id: 'trips', name: 'Trips', title: 'Customer dashboard — /trips', x: 502, y: 270, detail: 'The body map — every booked trip, its payment schedule, seats, changes, and activity feed.' },
];

const ORGANS = [
  { id: 'stripe', name: 'Stripe', title: 'Card rail + Radar', x: 296, y: 700, detail: 'Capital, Zero and Reserve subscriptions, webhooks into memory, Radar risk signals to Vera. 41 reflex arcs ride this rail.' },
  { id: 'nmi', name: 'NMI', title: 'Vault card rail', x: 388, y: 714, detail: 'The tokenized vault rail — daily 14:00 UTC charge runs, reconcilers every 15 minutes, webhooks into memory. 21 reflex arcs.' },
  { id: 'resend', name: 'Resend', title: 'The voice — transactional email', x: 502, y: 716, detail: 'Every automated word Defera speaks: receipts, notices, nurture, briefings — with open tracking on mail.defera.app and topic-level consent.' },
  { id: 'duffel', name: 'Search Engine 2', title: 'Airline inventory + orders', x: 148, y: 366, detail: 'Deep flight search fallback and the order chain Owen watches for airline confirmation numbers.' },
  { id: 'serpapi', name: 'Search Engine 1', title: 'Primary fare search', x: 168, y: 458, detail: 'Primary sight — live fares with hidden inventory on every call, calendar fare intelligence included.' },
  { id: 'openai', name: 'OpenAI', title: 'gpt-4o substrate', x: 196, y: 138, detail: 'The cortical substrate for the concierge desk and every narrative layer — Stephanie’s reviews, Iris’s strategy, the managers’ daily reports.' },
  { id: 'claude', name: 'Claude', title: 'Scheduled-agent substrate', x: 148, y: 226, detail: 'Powers Wren’s daily reliability reasoning — a scheduled agent that reads the codebase-shaped world and emails findings.' },
  { id: 'cartesia', name: 'Cartesia', title: 'Voice synthesis', x: 694, y: 616, detail: 'Turns the concierge desk into a phone line — same knowledge, same grounding, out loud.' },
  { id: 'vercel', name: 'Vercel', title: 'The body — host + cron heart', x: 772, y: 654, detail: 'Hosts the SPA and API, and fires all 22 cron schedules that keep the autonomic nervous system beating.' },
];

const WORLD = [
  { id: 'customers', name: 'Customers', title: 'The outside world', x: 908, y: 396, detail: 'Everything the brain exists for. They search, chat, call, book, and pay — every sense faces them.' },
  { id: 'founders', name: 'Founders', title: 'support@defera.app', x: 886, y: 118, detail: 'Twice-daily manager briefings (15:00 + 01:00 UTC), critical security alerts, and every report the desks file upward.' },
];

// The 22 vercel.json cron schedules, grouped into 8 pacemaker ganglia.
const PACEMAKERS = [
  { id: 'pm-sweep', name: 'Security sweep', x: 350, y: 648, period: 8.5, detail: 'silas-audit · every 6 hours' },
  { id: 'pm-reconcilers', name: 'Reconcilers', x: 396, y: 668, period: 2.6, detail: 'defera-capital-reconcile · */15\ndefera-capital-nmi-reconcile · */15\nnmi-reconcile-refunds · 16:30\ncancel-request-watchdog · 16:45\nfaye-policy-issue · */15' },
  { id: 'pm-billing', name: 'Billing cadence', x: 442, y: 680, period: 5.2, detail: 'payment reminders · 13:00\nnmi-charge-due-installments · 14:00\nsend-default-warnings · 15:00\npayment-trouble-check · 15:30' },
  { id: 'pm-audit', name: 'Hourly audit', x: 466, y: 634, period: 3.6, detail: 'audrey-audit · hourly at :45' },
  { id: 'pm-briefings', name: 'Manager briefings', x: 490, y: 684, period: 10.5, detail: 'manager-briefings · 15:00 + 01:00' },
  { id: 'pm-reports', name: 'Analyst reports', x: 538, y: 676, period: 6.4, detail: 'portfolio-snapshot · 07:30\nezra-insights · 09:00\niris-strategy · 10:00\nelliott-report · 11:00\nmargot-email-report · 12:00\nbernard-report · 12:30' },
  { id: 'pm-bench', name: 'Search benchmark', x: 584, y: 660, period: 4.4, detail: 'priya-search-benchmark · every 2h at :15' },
  { id: 'pm-marketing', name: 'Marketing window', x: 626, y: 638, period: 7.2, detail: 'send-bar-raised-emails · 16:00\nsend-destination-calling · 17:00\nsend-trending-digest · monthly\nbirthday-credit · 13:00' },
];

// Real counts surfaced in the HUD. 37 = vercel.json cron entries; 72 =
// supabase/functions dirs (less _shared). Update alongside those sources.
const RHYTHM_COUNT = 37;
const REFLEX_ARC_COUNT = 72;

// Edge kinds → pulse tints.
const KIND_TINTS = {
  hier: [205, 214, 238],
  data: [122, 152, 238],
  money: [116, 216, 164],
  comms: [242, 156, 182],
  sense: [116, 208, 228],
  rhythm: [238, 198, 124],
  substrate: [184, 156, 246],
  world: [228, 232, 246],
};

// [from, to, kind, flavor?, bowOverride?] — flavor strings feed the synapse
// log when an ambient pulse rides that edge.
const EDGE_DEFS = [
  // Reporting lines (real reportsTo from the roster)
  ['tommy', 'stephanie', 'hier'], ['camille', 'stephanie', 'hier'], ['andre', 'stephanie', 'hier'],
  ['isabella', 'stephanie', 'hier'], ['kai', 'stephanie', 'hier'],
  ['vera', 'elliott', 'hier'], ['marcus', 'elliott', 'hier'], ['roman', 'elliott', 'hier'],
  ['ezra', 'iris', 'hier'], ['priya', 'iris', 'hier'],
  ['penny', 'audrey', 'hier'], ['wren', 'silas', 'hier'],
  // Convergence on memory
  ['stephanie', 'supabase', 'data', 'review filed to memory'],
  ['elliott', 'supabase', 'data'], ['bernard', 'supabase', 'data', 'ledger read — cash vs accrual'],
  ['iris', 'supabase', 'data'], ['margot', 'supabase', 'data'],
  ['miles', 'supabase', 'data', 'miles ledger write'],
  ['audrey', 'supabase', 'data', 'heartbeat verified'], ['owen', 'supabase', 'data'],
  ['marcus', 'supabase', 'data', 'portfolio snapshot stored'], ['wren', 'supabase', 'data', 'reliability sweep query'],
  ['chat-desk', 'supabase', 'data', 'transcript committed to memory'],
  ['checkout', 'supabase', 'data'], ['search', 'supabase', 'data', 'zero-result event logged'],
  ['trips', 'supabase', 'data'], ['voice-desk', 'supabase', 'data'],
  // The outside world
  ['customers', 'chat-desk', 'world', 'customer message → concierge desk'],
  ['customers', 'voice-desk', 'world', 'inbound call'],
  ['customers', 'search', 'world', 'route searched'],
  ['customers', 'trips', 'world'],
  ['customers', 'checkout', 'world', 'installment plan started'],
  // Speech — email out
  ['margot', 'resend', 'comms', 'lifecycle send queued'],
  ['audrey', 'resend', 'comms', 'audit alarm drafted'],
  ['silas', 'resend', 'comms', 'critical finding → founders'],
  ['stephanie', 'resend', 'comms', 'support@ send voiced'],
  ['resend', 'customers', 'comms', 'email delivered'],
  ['resend', 'founders', 'comms', 'briefing delivered', 1],
  // Money
  ['checkout', 'stripe', 'money', 'capital installment authorized'],
  ['checkout', 'nmi', 'money', 'vault charge tokenized'],
  ['stripe', 'supabase', 'money', 'stripe webhook ingested'],
  ['nmi', 'supabase', 'money', 'nmi webhook ingested'],
  ['penny', 'nmi', 'money', 'charge run executed'],
  ['vera', 'stripe', 'money', 'radar signal read'],
  // Senses
  ['search', 'serpapi', 'sense', 'fare probe → search engine 1'],
  ['search', 'duffel', 'sense', 'search engine 2 availability sweep'],
  ['owen', 'duffel', 'sense', 'order chain polled for confirmation'],
  // Substrate
  ['openai', 'stephanie', 'substrate', 'review pass tokens streamed'],
  ['openai', 'iris', 'substrate'], ['openai', 'elliott', 'substrate'],
  ['openai', 'bernard', 'substrate'], ['openai', 'margot', 'substrate'],
  ['claude', 'wren', 'substrate', 'wren reasoning run'],
  // Autonomic rhythms
  ['pm-reconcilers', 'stripe', 'rhythm'], ['pm-reconcilers', 'nmi', 'rhythm'],
  ['pm-billing', 'penny', 'rhythm'], ['pm-audit', 'audrey', 'rhythm'],
  ['pm-sweep', 'silas', 'rhythm'],
  ['pm-reports', 'elliott', 'rhythm'], ['pm-reports', 'bernard', 'rhythm'],
  ['pm-reports', 'iris', 'rhythm'], ['pm-reports', 'ezra', 'rhythm'],
  ['pm-reports', 'margot', 'rhythm'], ['pm-reports', 'marcus', 'rhythm'],
  ['pm-bench', 'priya', 'rhythm'], ['pm-marketing', 'resend', 'rhythm'],
  ['pm-marketing', 'miles', 'rhythm'],
  ['pm-briefings', 'stephanie', 'rhythm'], ['pm-briefings', 'audrey', 'rhythm'],
  ['vercel', 'pm-marketing', 'rhythm'], ['vercel', 'pm-reconcilers', 'rhythm'],
  // Observation + operations
  ['ezra', 'chat-desk', 'data', 'concierge questions studied'],
  ['priya', 'search', 'data', 'search telemetry read'],
  ['voice-desk', 'cartesia', 'sense', 'voice synthesis stream'],
  ['silas', 'vercel', 'sense', 'external posture probe'],
  ['owen', 'trips', 'data'],
];

// How long each connection really takes, in ms. A pulse crosses its edge in
// time with this (log scale, pulseSeconds), so a faster link visibly fires
// faster, and the synapse log prints it. Measured where we log it; the rest
// are each service's typical round trip. Search: first results in ~1.7 s
// (the 6-route speed benchmark; a full search's median is 7.3 s in
// flight_search_log, Oct 28-30).
const KIND_LATENCY_MS = {
  hier: 60000, // managers review their team's work in batches, not live
  data: 40, // a Supabase read or write
  money: 800,
  comms: 300, // Resend accepts a send
  sense: 800,
  rhythm: 300, // a cron firing its job
  substrate: 8000, // an AI generation
  world: 150, // a customer's request reaching us
};
const EDGE_LATENCY_MS = {
  'search>serpapi': 1700,
  'search>duffel': 3000, // the deep-search fallback
  'owen>duffel': 800,
  'customers>voice-desk': 2000, // a call connecting
  'customers>checkout': 2000, // a plan's first charge clearing
  'resend>customers': 5000, // send to inbox
  'resend>founders': 5000,
  'checkout>stripe': 500,
  'vera>stripe': 500,
  'checkout>nmi': 1500,
  'penny>nmi': 1500,
  'stripe>supabase': 1000, // webhook delivery
  'nmi>supabase': 1000,
  'openai>stephanie': 8000, // a review pass
  'openai>iris': 30000, // full daily reports
  'openai>elliott': 30000,
  'openai>bernard': 30000,
  'openai>margot': 30000,
  'claude>wren': 120000, // a reasoning run over the codebase
  'voice-desk>cartesia': 300, // first audio of a spoken reply
  'silas>vercel': 400,
  'ezra>chat-desk': 60,
  'priya>search': 60,
};

/** Seconds a pulse takes to cross an edge whose real round trip is `ms`:
 *  40 ms ≈ 1.0 s, 1.7 s ≈ 2.8 s, 30 s ≈ 4.2 s (clamped 0.3–5 s). */
function pulseSeconds(ms) {
  const v = 0.35 + 1.1 * (Math.log10(Math.max(10, ms)) - 1);
  return Math.min(5, Math.max(0.3, v));
}

/** "40 ms", "1.7 s", "2 min". */
function formatLatency(ms) {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60000) return `${+(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms / 60000)} min`;
}

// Pacemaker fire → synapse log line.
const PACEMAKER_LOGS = {
  'pm-reconcilers': '15-min reconciler pass — capital + nmi',
  'pm-billing': 'billing cadence — reminders · charges · warnings',
  'pm-audit': 'audrey hourly audit heartbeat',
  'pm-sweep': 'silas security sweep begins',
  'pm-reports': 'analyst report window opens',
  'pm-bench': 'priya search benchmark tick',
  'pm-marketing': 'marketing window — bar-raised · destination',
  'pm-briefings': 'manager briefings compiled',
};

// ---------------------------------------------------------------------------
// Small deterministic helpers (stable layout across mounts).
// ---------------------------------------------------------------------------

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashStr(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

function pointInPolygon(x, y, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}

// Smoothed closed/open polyline via Catmull-Rom → bezier.
function traceSmooth(ctx, pts, closed) {
  const n = pts.length;
  if (n < 2) return;
  ctx.moveTo(pts[0][0], pts[0][1]);
  const last = closed ? n : n - 1;
  for (let i = 0; i < last; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const useP0 = closed || i > 0 ? p0 : p1;
    const useP3 = closed || i < n - 2 ? p3 : p2;
    ctx.bezierCurveTo(
      p1[0] + (p2[0] - useP0[0]) / 6, p1[1] + (p2[1] - useP0[1]) / 6,
      p2[0] - (useP3[0] - p1[0]) / 6, p2[1] - (useP3[1] - p1[1]) / 6,
      p2[0], p2[1],
    );
  }
  if (closed) ctx.closePath();
}

function rgba([r, g, b], a) { return `rgba(${r},${g},${b},${a})`; }

// ---------------------------------------------------------------------------
// Node/edge model construction (module-level: static by design).
// ---------------------------------------------------------------------------

function buildModel() {
  const nodes = [];
  const push = (n) => { nodes.push(n); return n; };

  for (const s of STAFF) {
    push({
      ...s, type: 'staff', r: s.manager ? 7 : 6,
      tint: DEPT_TINTS[s.dept], lobe: DEPT_LOBES[s.dept],
    });
  }
  push({ ...CORE, type: 'core', r: 11, tint: [191, 210, 255], lobe: 'Hippocampus — long-term memory' });
  for (const s of SURFACES) push({ ...s, type: 'surface', r: 5, tint: [168, 181, 219] });
  for (const o of ORGANS) push({ ...o, type: 'organ', r: 5.5, tint: [201, 212, 238] });
  for (const w of WORLD) push({ ...w, type: 'world', r: w.id === 'customers' ? 13 : 9, tint: [226, 231, 246] });
  // Left to right, the pacemaker labels cycle through four rows (below,
  // above, further below, further above) so neighbours never share one; the
  // leftmost sits beside its node, clear of the next one's label.
  const pmOrder = [...PACEMAKERS].sort((a, b) => a.x - b.x).map((p) => p.id);
  for (const p of PACEMAKERS) {
    push({
      ...p, type: 'pacemaker', r: 3.5, tint: [232, 200, 143],
      title: 'Autonomic rhythm', lobe: 'Brainstem ganglion',
      _labelRow: pmOrder.indexOf(p.id) === 0 ? 'left' : pmOrder.indexOf(p.id) % 4,
    });
  }

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const edges = EDGE_DEFS.map(([from, to, kind, flavor, bowSign]) => {
    const a = byId.get(from);
    const b = byId.get(to);
    const h = hashStr(`${from}>${to}`);
    return {
      a, b, kind, flavor: flavor || null,
      tint: KIND_TINTS[kind],
      latencyMs: EDGE_LATENCY_MS[`${from}>${to}`] ?? KIND_LATENCY_MS[kind],
      // Perpendicular bow: hash-signed unless overridden, scaled by length.
      bow: (bowSign || (h % 2 === 0 ? 1 : -1)) * (0.10 + (h % 37) / 370),
      key: `${from}>${to}`,
    };
  }).filter((e) => e.a && e.b);

  // Neighbor map for hover emphasis.
  const neighbors = new Map(nodes.map((n) => [n.id, new Set([n.id])]));
  for (const e of edges) {
    neighbors.get(e.a.id).add(e.b.id);
    neighbors.get(e.b.id).add(e.a.id);
  }

  // Decorative dust neurons inside the silhouette (seeded, stable) — the
  // fine cortical web that makes the tissue read dense.
  const rng = mulberry32(0xD3F3A);
  const dust = [];
  let guard = 0;
  while (dust.length < 210 && guard++ < 6000) {
    const x = 130 + rng() * 700;
    const y = 90 + rng() * 570;
    if (!pointInPolygon(x, y, BRAIN_OUTLINE)) continue;
    if (nodes.some((n) => (n.x - x) ** 2 + (n.y - y) ** 2 < 24 * 24)) continue;
    dust.push({ x, y, tw: rng() * Math.PI * 2, sp: 0.4 + rng() * 0.9 });
  }
  const dustLinks = [];
  for (let i = 0; i < dust.length; i++) {
    const near = dust
      .map((d, j) => ({ j, d2: (d.x - dust[i].x) ** 2 + (d.y - dust[i].y) ** 2 }))
      .filter((o) => o.j !== i && o.d2 < 84 * 84)
      .sort((p, q) => p.d2 - q.d2)
      .slice(0, 3);
    for (const o of near) if (i < o.j) dustLinks.push([i, o.j]);
  }

  // Background starfield across the whole stage (drawn dimmer outside brain).
  const stars = Array.from({ length: 90 }, () => ({
    x: rng() * STAGE_W, y: rng() * STAGE_H, tw: rng() * Math.PI * 2, r: 0.5 + rng(),
  }));

  return { nodes, byId, edges, neighbors, dust, dustLinks, stars };
}

const MODEL = buildModel();

// ---------------------------------------------------------------------------
// The canvas.
// ---------------------------------------------------------------------------

function BrainCanvas({ onActiveNode, activeIdRef, reducedMotion, pushLog }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext('2d');

    let raf = 0;
    let disposed = false;
    const view = { w: 0, h: 0, s: 1, ox: 0, oy: 0, dpr: 1 };
    const pulses = [];
    const rings = [];
    const activity = new Map(); // node id -> flare 0..1
    const pmTimers = MODEL.nodes
      .filter((n) => n.type === 'pacemaker')
      .map((n) => ({ node: n, next: 1.5 + Math.random() * n.period }));
    // Simulation clock: accumulated with a clamped per-frame step, so a
    // backgrounded tab (rAF suspended) pauses instead of firing every
    // pacemaker at once on wake. Also makes pulse speed refresh-rate-proof.
    let tAnim = 0;
    let lastNow = performance.now();
    let lastSpawn = 0;

    const resize = () => {
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      view.w = w; view.h = h; view.dpr = dpr;
      view.s = Math.min(w / STAGE_W, h / (STAGE_H + 20));
      view.ox = (w - STAGE_W * view.s) / 2;
      view.oy = (h - STAGE_H * view.s) / 2;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    // Drifted stage position for a node at time t.
    const posOf = (n, t) => {
      if (reducedMotion) return [n.x, n.y];
      const h = hashStr(n.id);
      const amp = n.type === 'staff' || n.type === 'surface' ? 3.2 : n.type === 'core' ? 2 : 1.5;
      return [
        n.x + Math.sin(t * (0.25 + (h % 13) / 40) + h) * amp,
        n.y + Math.cos(t * (0.21 + (h % 7) / 30) + h * 1.7) * amp,
      ];
    };

    const edgeGeometry = (e, t) => {
      const [ax, ay] = posOf(e.a, t);
      const [bx, by] = posOf(e.b, t);
      const dx = bx - ax; const dy = by - ay;
      const len = Math.hypot(dx, dy) || 1;
      const bow = Math.max(10, Math.min(64, len * Math.abs(e.bow))) * Math.sign(e.bow);
      const cx = (ax + bx) / 2 + (-dy / len) * bow;
      const cy = (ay + by) / 2 + (dx / len) * bow;
      return { ax, ay, bx, by, cx, cy };
    };

    const bezPoint = (g, t) => {
      const u = 1 - t;
      return [
        u * u * g.ax + 2 * u * t * g.cx + t * t * g.bx,
        u * u * g.ay + 2 * u * t * g.cy + t * t * g.by,
      ];
    };

    const spawnPulse = (e, reverse = false) => {
      // Crosses in time with the link's real latency, ±8% so pulses on
      // one edge don't march in lockstep.
      pulses.push({ e, t: 0, speed: (1 / pulseSeconds(e.latencyMs)) * (0.92 + Math.random() * 0.16), reverse });
    };

    const firePacemaker = (pm) => {
      rings.push({ node: pm.node, t: 0 });
      activity.set(pm.node.id, 1);
      const out = MODEL.edges.filter((e) => e.a.id === pm.node.id);
      out.forEach((e, i) => setTimeout(() => { if (!disposed) spawnPulse(e); }, i * 140));
      pushLog(PACEMAKER_LOGS[pm.node.id] || `${pm.node.name} fired`, KIND_TINTS.rhythm);
    };

    // Poke: fire every edge touching a node (used on click-pin).
    canvas._poke = (id) => {
      activity.set(id, 1);
      for (const e of MODEL.edges) {
        if (e.a.id === id) spawnPulse(e);
        else if (e.b.id === id) spawnPulse(e, true);
      }
    };

    const draw = (now) => {
      const dt = Math.min(0.05, Math.max(0.001, (now - lastNow) / 1000));
      lastNow = now;
      tAnim += dt;
      const t = tAnim;
      const { w, h, s, ox, oy, dpr } = view;
      const activeId = activeIdRef.current;
      const neigh = activeId ? MODEL.neighbors.get(activeId) : null;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      // Deep-space base + royal vignette.
      ctx.fillStyle = '#04050a';
      ctx.fillRect(0, 0, w, h);
      const vg = ctx.createRadialGradient(w * 0.5, h * 0.42, 0, w * 0.5, h * 0.42, Math.max(w, h) * 0.62);
      vg.addColorStop(0, 'rgba(16,37,178,0.10)');
      vg.addColorStop(0.55, 'rgba(10,18,51,0.05)');
      vg.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, w, h);

      ctx.translate(ox, oy);
      ctx.scale(s, s);

      // Starfield.
      for (const st of MODEL.stars) {
        const a = 0.10 + 0.10 * (reducedMotion ? 0.5 : Math.abs(Math.sin(t * 0.6 + st.tw)));
        ctx.fillStyle = `rgba(200,212,245,${a.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Halo behind the brain.
      const bg = ctx.createRadialGradient(470, 370, 40, 470, 370, 420);
      bg.addColorStop(0, 'rgba(61,90,140,0.10)');
      bg.addColorStop(1, 'rgba(61,90,140,0)');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, STAGE_W, STAGE_H);

      // Silhouette + folds.
      ctx.beginPath();
      traceSmooth(ctx, BRAIN_OUTLINE, true);
      ctx.strokeStyle = 'rgba(168,181,219,0.13)';
      ctx.lineWidth = 1.1;
      ctx.stroke();
      const og = ctx.createRadialGradient(470, 360, 80, 470, 360, 380);
      og.addColorStop(0, 'rgba(36,59,122,0.10)');
      og.addColorStop(1, 'rgba(36,59,122,0.015)');
      ctx.fillStyle = og;
      ctx.fill();
      for (const fold of SULCI) {
        ctx.beginPath();
        traceSmooth(ctx, fold, false);
        ctx.strokeStyle = 'rgba(200,212,245,0.05)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Dust web.
      ctx.lineWidth = 0.6;
      for (const [i, j] of MODEL.dustLinks) {
        const a = MODEL.dust[i]; const b = MODEL.dust[j];
        ctx.strokeStyle = 'rgba(168,181,219,0.07)';
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      for (const d of MODEL.dust) {
        const a = 0.10 + 0.14 * (reducedMotion ? 0.5 : Math.abs(Math.sin(t * d.sp + d.tw)));
        ctx.fillStyle = `rgba(200,212,245,${a.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(d.x, d.y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }

      // Synapses.
      const geoms = new Map();
      for (const e of MODEL.edges) {
        const g = edgeGeometry(e, t);
        geoms.set(e.key, g);
        const touches = neigh && (e.a.id === activeId || e.b.id === activeId);
        const dimmed = neigh && !touches;
        const alpha = touches ? 0.45 : dimmed ? 0.028 : 0.11;
        ctx.strokeStyle = touches ? rgba(e.tint, alpha) : `rgba(168,181,219,${alpha})`;
        ctx.lineWidth = touches ? 1.3 : 0.7;
        ctx.beginPath();
        ctx.moveTo(g.ax, g.ay);
        ctx.quadraticCurveTo(g.cx, g.cy, g.bx, g.by);
        ctx.stroke();
      }

      // Pulses (additive comets).
      if (!reducedMotion) {
        ctx.globalCompositeOperation = 'lighter';
        for (let i = pulses.length - 1; i >= 0; i--) {
          const p = pulses[i];
          p.t += p.speed * dt;
          if (p.t >= 1) {
            const target = p.reverse ? p.e.a : p.e.b;
            activity.set(target.id, 1);
            pulses.splice(i, 1);
            continue;
          }
          const g = geoms.get(p.e.key);
          if (!g) continue;
          const tt = p.reverse ? 1 - p.t : p.t;
          const [px, py] = bezPoint(g, tt);
          const [qx, qy] = bezPoint(g, Math.max(0, Math.min(1, tt + (p.reverse ? 0.05 : -0.05))));
          const grad = ctx.createLinearGradient(qx, qy, px, py);
          grad.addColorStop(0, rgba(p.e.tint, 0));
          grad.addColorStop(1, rgba(p.e.tint, 0.5));
          ctx.strokeStyle = grad;
          ctx.lineWidth = 1.4;
          ctx.beginPath();
          ctx.moveTo(qx, qy);
          ctx.lineTo(px, py);
          ctx.stroke();
          ctx.fillStyle = rgba(p.e.tint, 0.85);
          ctx.beginPath();
          ctx.arc(px, py, 1.7, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = rgba(p.e.tint, 0.16);
          ctx.beginPath();
          ctx.arc(px, py, 5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalCompositeOperation = 'source-over';
      }

      // Pacemaker beat rings.
      for (let i = rings.length - 1; i >= 0; i--) {
        const r = rings[i];
        r.t += dt;
        const k = r.t / 1.4;
        if (k >= 1) { rings.splice(i, 1); continue; }
        const [px, py] = posOf(r.node, t);
        ctx.strokeStyle = rgba(KIND_TINTS.rhythm, 0.35 * (1 - k));
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(px, py, 4 + k * 26, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Nodes. Every node is labelled; on a small screen the labels shrink
      // with the map (never below 7px) rather than disappearing.
      const labelScale = Math.min(1, Math.max(0.74, s / 0.75));
      for (const n of MODEL.nodes) {
        const [px, py] = posOf(n, t);
        n._px = px; n._py = py; // cached for hit-testing / tooltip anchor
        const flare = activity.get(n.id) || 0;
        if (flare > 0.02) activity.set(n.id, flare * Math.pow(0.06, dt));
        const isActive = activeId === n.id;
        const inNeigh = !neigh || neigh.has(n.id);
        const dim = inNeigh ? 1 : 0.3;
        const breathe = n.type === 'core' && !reducedMotion ? 1 + 0.05 * Math.sin(t * 1.4) : 1;
        const R = n.r * breathe * (1 + flare * 0.45);

        // Glow.
        const glowA = (n.type === 'world' ? 0.10 : 0.16) + flare * 0.30;
        const gr = ctx.createRadialGradient(px, py, 0, px, py, R * (n.type === 'core' ? 4.2 : 3.4));
        gr.addColorStop(0, rgba(n.tint, glowA * dim));
        gr.addColorStop(1, rgba(n.tint, 0));
        ctx.fillStyle = gr;
        ctx.beginPath();
        ctx.arc(px, py, R * (n.type === 'core' ? 4.2 : 3.4), 0, Math.PI * 2);
        ctx.fill();

        if (n.type === 'organ') {
          // Hollow organ with a slowly rotating dashed ring.
          ctx.strokeStyle = rgba(n.tint, 0.75 * dim);
          ctx.lineWidth = 1.4;
          ctx.beginPath();
          ctx.arc(px, py, R, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = rgba(n.tint, 0.9 * dim);
          ctx.beginPath();
          ctx.arc(px, py, 1.8, 0, Math.PI * 2);
          ctx.fill();
          ctx.save();
          ctx.setLineDash([2.5, 4.5]);
          ctx.lineDashOffset = reducedMotion ? 0 : -t * 6;
          ctx.strokeStyle = rgba(n.tint, 0.28 * dim);
          ctx.lineWidth = 0.9;
          ctx.beginPath();
          ctx.arc(px, py, R + 4.5, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        } else if (n.type === 'world') {
          const wg = ctx.createRadialGradient(px, py, 0, px, py, R);
          wg.addColorStop(0, rgba(n.tint, 0.55 * dim));
          wg.addColorStop(1, rgba(n.tint, 0.06 * dim));
          ctx.fillStyle = wg;
          ctx.beginPath();
          ctx.arc(px, py, R, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = rgba(n.tint, (n.type === 'pacemaker' ? 0.85 : 0.95) * dim);
          ctx.beginPath();
          ctx.arc(px, py, R, 0, Math.PI * 2);
          ctx.fill();
          if (n.type === 'core' || n.manager) {
            ctx.strokeStyle = rgba(n.tint, 0.4 * dim);
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(px, py, R + 3.5, 0, Math.PI * 2);
            ctx.stroke();
          }
        }

        if (isActive) {
          ctx.strokeStyle = rgba([255, 255, 255], 0.75);
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.arc(px, py, R + 6.5, 0, Math.PI * 2);
          ctx.stroke();
        }

        // Label. The pacemakers sit in a tight row, so theirs spread over
        // four rows (_labelRow) to keep neighbours from colliding.
        const emphasized = neigh && neigh.has(n.id);
        const la = !neigh ? (n.type === 'staff' ? 0.55 : 0.45) : emphasized ? 0.92 : 0.14;
        const size = (n.type === 'core' || n.type === 'world' ? 10.5 : 9.5) * labelScale;
        const row = n.type === 'pacemaker' ? n._labelRow : 0;
        ctx.font = `500 ${size.toFixed(1)}px Inter, ui-sans-serif, system-ui`;
        ctx.fillStyle = `rgba(226,231,246,${la})`;
        if (row === 'left') {
          ctx.textAlign = 'right';
          ctx.fillText(n.name, px - R - 4 * labelScale, py + size * 0.35);
        } else {
          const below = row === 0 || row === 2;
          const far = row >= 2 ? size + 2 : 0;
          ctx.textAlign = 'center';
          ctx.fillText(n.name, px, below ? py + R + 12 * labelScale + far : py - R - 6 * labelScale - far);
        }
      }

      // Ambient pulse spawner.
      if (!reducedMotion && pulses.length < 70 && t - lastSpawn > 0.10) {
        lastSpawn = t;
        if (Math.random() < 0.5) {
          const e = MODEL.edges[(Math.random() * MODEL.edges.length) | 0];
          if (e.kind !== 'rhythm') {
            spawnPulse(e, e.kind === 'data' && Math.random() < 0.25);
            if (e.flavor && Math.random() < 0.22) pushLog(`${e.flavor} · ${formatLatency(e.latencyMs)}`, e.tint);
          }
        }
      }

      // Pacemaker clocks.
      if (!reducedMotion) {
        for (const pm of pmTimers) {
          if (t >= pm.next) {
            pm.next = t + pm.node.period * (0.85 + Math.random() * 0.3);
            firePacemaker(pm);
          }
        }
      }

      onActiveNode.syncTooltip?.();
      if (!disposed) raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);

    // --- Pointer interaction ------------------------------------------------
    const stagePoint = (ev) => {
      const rect = canvas.getBoundingClientRect();
      return [(ev.clientX - rect.left - view.ox) / view.s, (ev.clientY - rect.top - view.oy) / view.s];
    };
    const hitTest = (sx, sy) => {
      let best = null;
      let bestD = (18 / Math.min(1, view.s)) ** 2;
      for (const n of MODEL.nodes) {
        const d = (n._px ?? n.x) - sx;
        const d2 = ((n._py ?? n.y) - sy) ** 2 + d * d;
        const reach = bestD + n.r * n.r;
        if (d2 < reach && d2 < bestD + n.r * n.r) { best = n; bestD = d2 - n.r * n.r; }
      }
      return best;
    };
    const onMove = (ev) => {
      const [sx, sy] = stagePoint(ev);
      onActiveNode.hover(hitTest(sx, sy), ev.clientX, ev.clientY);
    };
    const onDown = (ev) => {
      const [sx, sy] = stagePoint(ev);
      const n = hitTest(sx, sy);
      onActiveNode.pin(n, ev.clientX, ev.clientY);
      if (n && !reducedMotion) canvas._poke(n.id);
    };
    const onLeave = () => onActiveNode.hover(null, 0, 0);
    canvas.addEventListener('pointermove', onMove);
    // mousemove fallback for environments that don't synthesize pointer
    // events; duplicate delivery is harmless (same idempotent handler).
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointerleave', onLeave);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerleave', onLeave);
    };
    // The model and handlers are stable by construction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reducedMotion]);

  return (
    <div ref={wrapRef} className="absolute inset-0">
      <canvas ref={canvasRef} className="block w-full h-full cursor-crosshair" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page shell: gates, HUD, tooltip, synapse log.
// ---------------------------------------------------------------------------

export default function Brain() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [liveById, setLiveById] = useState(null);
  const [utc, setUtc] = useState('');
  const [log, setLog] = useState([]);
  const [tooltip, setTooltip] = useState(null); // {node, x, y, pinned}
  const activeIdRef = useRef(null);
  const logRef = useRef([]);
  const logDirtyRef = useRef(false);

  const reducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await UserEntity.me();
        if (!cancelled) setUser(me || null);
      } catch {
        /* signed out */
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const admin = isCurrentUserAdmin(user);

  // Live roster overlay — requireAdmin-gated server-side; static map is the
  // fallback, so a failed fetch only means slightly staler tooltip copy.
  useEffect(() => {
    if (!authReady || !admin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const tok = data?.session?.access_token;
        if (!tok) return;
        const res = await fetch('/api/admin/staff', { headers: { Authorization: `Bearer ${tok}` } });
        const body = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && body?.ok && Array.isArray(body.staff)) {
          setLiveById(new Map(body.staff.map((m) => [m.id, m])));
        }
      } catch {
        /* static fallback */
      }
    })();
    return () => { cancelled = true; };
  }, [authReady, admin]);

  // UTC clock — the whole autonomic system runs on UTC.
  useEffect(() => {
    const tick = () => setUtc(new Date().toISOString().slice(11, 19));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Synapse log: written from the anim loop into a ref, flushed to React on
  // a slow interval so the canvas never renders against setState churn.
  const pushLog = useCallback((text, tint) => {
    logRef.current = [
      { id: `${Date.now()}-${Math.random()}`, at: new Date().toISOString().slice(11, 19), text, tint },
      ...logRef.current,
    ].slice(0, 5);
    logDirtyRef.current = true;
  }, []);
  useEffect(() => {
    const id = setInterval(() => {
      if (logDirtyRef.current) {
        logDirtyRef.current = false;
        setLog(logRef.current);
      }
    }, 700);
    return () => clearInterval(id);
  }, []);

  // Hover/pin plumbing shared with the canvas.
  const onActiveNode = useMemo(() => {
    const api = {
      hover(node, cx, cy) {
        if (api._pinned) return;
        const nextId = node ? node.id : null;
        if (activeIdRef.current !== nextId) activeIdRef.current = nextId;
        setTooltip((prev) => {
          if (!node) return prev && !prev.pinned ? null : prev;
          return { node, x: cx, y: cy, pinned: false };
        });
      },
      pin(node, cx, cy) {
        if (!node) {
          api._pinned = false;
          activeIdRef.current = null;
          setTooltip(null);
          return;
        }
        if (api._pinned && activeIdRef.current === node.id) {
          api._pinned = false;
          setTooltip({ node, x: cx, y: cy, pinned: false });
          return;
        }
        api._pinned = true;
        activeIdRef.current = node.id;
        setTooltip({ node, x: cx, y: cy, pinned: true });
      },
      syncTooltip: null,
      _pinned: false,
    };
    return api;
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onActiveNode._pinned = false;
        activeIdRef.current = null;
        setTooltip(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onActiveNode]);

  // --- Gates ---------------------------------------------------------------

  if (!authReady) {
    return (
      <div className="fixed inset-0 bg-[#04050a] text-white flex items-center justify-center">
        <Helmet><title>Brain — Defera Admin</title><meta name="robots" content="noindex" /></Helmet>
        <div className="flex flex-col items-center gap-4">
          <span className="w-2 h-2 rounded-full bg-brand-300 animate-ping" />
          <p className="text-[11px] uppercase tracking-[0.28em] text-white/40 font-normal">Establishing synaptic link</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="fixed inset-0 bg-[#04050a] text-white flex items-center justify-center px-4">
        <Helmet><title>Brain — Defera Admin</title><meta name="robots" content="noindex" /></Helmet>
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-3">The Brain</h1>
          <p className="text-sm text-white/60 mb-4">Sign in to continue.</p>
          <Button asChild size="sm" className="bg-white text-black hover:bg-white/90">
            <Link to="/home?auth_mode=login">Sign in</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="fixed inset-0 bg-[#04050a] text-white flex items-center justify-center px-4">
        <Helmet><title>Brain — Defera Admin</title><meta name="robots" content="noindex" /></Helmet>
        <div className="max-w-md w-full">
          <h1 className="text-2xl font-semibold mb-4">The Brain</h1>
          <div className="rounded-2xl border border-red-400/30 bg-white/[0.02] p-4 flex items-start gap-2.5">
            <ShieldAlert className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-white mb-0.5">This page is restricted</p>
              <p className="text-white/60 text-xs">
                The Brain is an internal admin surface. If you think you should have access, contact support.
              </p>
              <div className="mt-3">
                <Button asChild size="sm" variant="outline" className="h-7 text-xs text-white border-white/20 bg-transparent hover:bg-white/10">
                  <Link to="/dashboard">Back to Trips</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- The living map ------------------------------------------------------

  const tnode = tooltip?.node || null;
  const live = tnode && liveById?.get(tnode.id);
  const ttTitle = live?.title || tnode?.title;
  const ttDetail = live?.summary || tnode?.detail;
  const ttX = tooltip ? Math.min(Math.max(tooltip.x + 16, 12), (typeof window !== 'undefined' ? window.innerWidth : 1200) - 312) : 0;
  const ttY = tooltip ? Math.min(Math.max(tooltip.y + 14, 12), (typeof window !== 'undefined' ? window.innerHeight : 800) - 220) : 0;

  return (
    <div className="fixed inset-0 bg-[#04050a] text-white overflow-hidden select-none">
      <Helmet>
        <title>Brain — Defera Admin</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <BrainCanvas
        onActiveNode={onActiveNode}
        activeIdRef={activeIdRef}
        reducedMotion={reducedMotion}
        pushLog={pushLog}
      />

      {/* Header */}
      <div className="absolute top-0 left-0 p-4 sm:p-6 pointer-events-none">
        <p className="text-[10px] uppercase tracking-[0.28em] text-white/35 font-normal mb-1.5 flex items-center gap-1.5">
          <BrainIcon className="w-3 h-3" strokeWidth={2} />
          Defera · Internal cortex
        </p>
        <h1 className="text-[26px] sm:text-[34px] font-thin tracking-tight leading-none">The Brain</h1>
        <p className="mt-2 text-[11.5px] text-white/45 max-w-[300px] leading-relaxed hidden sm:block">
          The whole organism on one synaptic map — every mind, rhythm, and rail, firing live.
        </p>
      </div>

      {/* Clock + counts + exit */}
      <div className="absolute top-0 right-0 p-4 sm:p-6 pointer-events-none flex flex-col items-end gap-1.5">
        <Link
          to="/staff"
          title="Exit to Staff"
          className="pointer-events-auto inline-flex items-center justify-center w-7 h-7 rounded-full border border-white/15 bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/10 transition"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
        </Link>
        <p className="font-mono text-[12px] text-white/55 tabular-nums">{utc} UTC</p>
        <p className="text-[10.5px] text-white/35 hidden sm:block text-right">
          {STAFF.length} minds · 9 lobes · {MODEL.edges.length} synapses · {RHYTHM_COUNT} rhythms · {REFLEX_ARC_COUNT} reflex arcs
        </p>
        <p className="text-[10px] text-emerald-300/60 flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full bg-emerald-400/80 ${reducedMotion ? '' : 'animate-pulse'}`} />
          Synaptic activity nominal
        </p>
      </div>

      {/* Synapse log */}
      <div className="absolute bottom-0 left-0 p-4 sm:p-6 pointer-events-none max-w-[340px]">
        <p className="text-[9.5px] uppercase tracking-[0.24em] text-white/30 font-medium mb-1.5">Synapse log</p>
        <div className="space-y-1">
          {reducedMotion ? (
            <p className="font-mono text-[10px] text-white/40">motion reduced — static map</p>
          ) : log.length === 0 ? (
            <p className="font-mono text-[10px] text-white/30">listening…</p>
          ) : (
            log.map((l, i) => (
              <p key={l.id} className="font-mono text-[10px] tabular-nums truncate" style={{ opacity: Math.max(0.25, 1 - i * 0.2) }}>
                <span className="text-white/35">{l.at}</span>{' '}
                <span style={{ color: rgba(l.tint, 0.85) }}>{l.text}</span>
              </p>
            ))
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="absolute bottom-0 right-0 p-4 sm:p-6 pointer-events-none text-right hidden sm:block">
        <div className="flex items-center justify-end gap-3.5 text-[10px] text-white/45 mb-1.5">
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: 'rgb(143,182,255)' }} />Minds</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: 'rgb(191,210,255)' }} />Memory</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full border border-[rgb(201,212,238)]" />Organs</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: 'rgb(232,200,143)' }} />Rhythms</span>
        </div>
        <p className="text-[10px] text-white/30">Hover to inspect · click to pin · Esc to release</p>
        <p className="text-[10px] text-white/25 mt-0.5">Autonomic rhythms compressed for display</p>
      </div>

      {/* Tooltip */}
      {tnode && (
        <div
          className="absolute z-20 w-[300px] rounded-xl border border-white/10 bg-black/85 backdrop-blur-md p-3.5 pointer-events-none shadow-2xl"
          style={{ left: ttX, top: ttY }}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: rgba(tnode.tint, 0.95) }} />
            <p className="text-[13px] font-medium leading-none">{tnode.name}</p>
            {tooltip.pinned && <span className="ml-auto text-[9px] uppercase tracking-[0.18em] text-white/35">pinned</span>}
          </div>
          {ttTitle && <p className="text-[11px] text-white/55 mb-1.5">{ttTitle}</p>}
          {tnode.lobe && (
            <p className="text-[9.5px] uppercase tracking-[0.16em] mb-2" style={{ color: rgba(tnode.tint, 0.75) }}>
              {tnode.lobe}
            </p>
          )}
          {ttDetail && (
            <p className="text-[11.5px] leading-relaxed text-white/60 whitespace-pre-line">{ttDetail}</p>
          )}
        </div>
      )}
    </div>
  );
}
